import type { SpaceAdapterRuntimeController } from '@toolproof-core/visualization/adapter/_contract/runtime';

import { createBrowserEpisodeNarrationOwner } from '@/episodeNarration';
import { createEpisodePlaybackEffectOwner } from '@/episodePlayback';
import { createConceptCyclePlaybackOwner } from '@/workflow-runner/conceptCyclePlayback';
import { conceptPresentationAt, episodeBeatSection, episodeSubtitleSections, planAnchorIndex, planBeatOpeningIndex, reduceEpisodeSubtitleIntent, resolveEpisodeBeatSelection, withSceneSubtitle } from '@/_pure/episode';
import { detachWorkflowEpisode } from '@/workflow-runner/_pure/episode';
import { resolveWorkflowSceneDirection } from '@/workflow-runner/_pure/sceneDirection';
import { getAvailableWorkflowEpisodes, resolveWorkflowEpisode, resolveWorkflowScenePlan, workflowEpisodes } from '@/workflow-runner/episodeCatalog';
import { withWorkflowSceneConfig } from '@/workflow-runner/episodeConfig';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';
import type { Config } from '@/workflow-runner/_contract/config';
import type { RuntimeState } from '@/workflow-runner/_contract/runtime';

type Controller = SpaceAdapterRuntimeController<Config, RuntimeState, CosmosDataModel>;
type Args = { config: Config; runtimeState: RuntimeState; cosmosData: CosmosDataModel };

function readingState(runtimeState: RuntimeState): RuntimeState {
    if (!runtimeState.episode) return runtimeState;
    return { ...runtimeState, isPlaying: false, playbackAdvanceMode: 'manual', accumulatedMs: 0,
        episode: { ...runtimeState.episode, status: 'reading' } };
}

function moveToBeat(args: Args, base: Controller, beatIndex: number, immediate = false): RuntimeState {
    const playback = args.runtimeState.episode;
    if (!playback) return args.runtimeState;
    const index = args.runtimeState.sceneIndex ?? 0;
    const { episode, scene } = resolveWorkflowEpisode(playback.episodeHandle, index);
    if (beatIndex >= scene.beats.length && index < episode.scenes.length - 1) {
        const next = enterScene(args, base, playback.episodeHandle, index + 1, playback.isPlaying);
        const selected = { ...next, episode: next.episode ? { ...next.episode,
            narrationRequested: playback.narrationRequested } : undefined };
        return immediate ? moveToBeat({ ...args, runtimeState: selected }, base, 0, true) : selected;
    }
    if (beatIndex < 0 && index > 0) {
        const previous = enterScene(args, base, playback.episodeHandle, index - 1);
        return moveToBeat({ ...args, runtimeState: { ...previous, episode: previous.episode
            ? { ...previous.episode, narrationRequested: playback.narrationRequested } : undefined } },
        base, episode.scenes[index - 1]!.beats.length - 1, immediate);
    }
    const beat = scene.beats[beatIndex];
    if (!beat) return { ...readingState(args.runtimeState), episode: { ...playback,
        isPlaying: false, status: 'reading', narrationRequested: false } };
    let state: RuntimeState = { ...args.runtimeState, conceptSum: undefined, conceptTool: undefined, conceptInvocation: undefined,
        isPlaying: false, playbackAdvanceMode: 'manual',
        ...(immediate ? { pendingReplayMove: null, accumulatedMs: 0 } : {}),
        episode: { ...playback, beatIndex, status: 'moving', narrationCompleted: false, cueIndex: 0,
            narrationCueReached: undefined, subtitleIndex: undefined,
            revision: playback.revision + 1, error: undefined } };
    if (scene.sceneKind === 'concept') {
        return readingState({ ...state, conceptArrangement: conceptPresentationAt(scene.beats[beatIndex]!, state.episode!).arrangement });
    }
    const replayFrom = scene.beats[beatIndex]!.replayFrom;
    if (immediate || replayFrom) {
        const { plan, replay, openingStepIndex } = resolveWorkflowScenePlan(args.cosmosData, state, playback.episodeHandle);
        state = { ...state, pendingReplayMove: null, accumulatedMs: 0,
            traceSelection: { ...state.traceSelection,
                replayPositionIndex: planBeatOpeningIndex(plan, replay, beatIndex, openingStepIndex) } };
    }
    return advanceTowards({ ...args, runtimeState: state }, base, traceTarget(args.cosmosData, state));
}

/** A trace beat's next stop: its pending cue's anchor, or its own landmark once every cue has been released. */
function traceTarget(cosmosData: CosmosDataModel, state: RuntimeState): number {
    const playback = state.episode!;
    const { plan, replay } = resolveWorkflowScenePlan(cosmosData, state, playback.episodeHandle);
    const beat = plan.beats[playback.beatIndex];
    const cue = beat?.cues?.[playback.cueIndex ?? 0];
    if (cue) return planAnchorIndex(replay, cue);
    const target = replay.landmarks.findIndex((landmark) => landmark.id === beat?.landmark);
    if (target < 0) throw new Error('Workflow Episode requires an exact replay landmark.');
    return target;
}

function enterScene(args: Args, base: Controller, handle: string, sceneIndex: number,
    automatic = false): RuntimeState {
    const { scene } = resolveWorkflowEpisode(handle, sceneIndex);
    const previous = args.runtimeState;
    const state: RuntimeState = { ...previous, isPlaying: false, playbackAdvanceMode: 'manual',
        accumulatedMs: 0, pendingReplayMove: null, sceneIndex,
        sceneOperationPresentationMode: scene.sceneKind === 'trace'
            ? scene.operationPresentationMode : undefined,
        sceneDirectionCatalogs: scene.sceneKind === 'trace' && scene.direction
            ? resolveWorkflowSceneDirection(scene.direction) : undefined,
        conceptArrangement: undefined, conceptSum: undefined, conceptTool: undefined, conceptInvocation: undefined, explorationBeforeConcept: undefined,
        episode: { episodeHandle: handle, beatIndex: 0, status: 'reading', cueIndex: 0,
            playbackAdvanceMode: automatic ? 'automatic' : 'manual', isPlaying: automatic,
            soundEnabled: previous.episode?.soundEnabled ?? args.config.episodeSoundDefault ?? false,
            subtitlesEnabled: previous.episode?.subtitlesEnabled ?? true,
            ...(previous.episode?.subtitleTrace ? { subtitleTrace: previous.episode.subtitleTrace } : {}),
            revision: (previous.episode?.revision ?? 0) + 1 } };
    if (scene.sceneKind === 'concept') {
        return { ...state, conceptArrangement: conceptPresentationAt(scene.beats[0]!, state.episode!).arrangement,
            explorationBeforeConcept: previous.explorationBeforeConcept ?? detachWorkflowEpisode(previous) };
    }
    const { plan, openingStepIndex } = resolveWorkflowScenePlan(args.cosmosData, state, handle, sceneIndex);
    const selected = { ...state, traceSelection: {
        workflowTraceSelectionKind: 'by-handle' as const, workflowTraceHandle: plan.trace.handle, replayPositionIndex: openingStepIndex } };
    return traceTarget(args.cosmosData, selected) === openingStepIndex
        ? selected : moveToBeat({ ...args, runtimeState: selected }, base, 0);
}

function advanceTowards(args: Args, base: Controller, target: number): RuntimeState {
    const state = args.runtimeState;
    if (state.pendingReplayMove) return state;
    const current = state.traceSelection.replayPositionIndex ?? 0;
    if (current === target) return readingState(state);
    const next = base.reduceOverlayIntent?.({ ...args, config: withWorkflowSceneConfig(args.config, state),
        runtimeState: { ...state, playbackAdvanceMode: 'manual', isPlaying: false },
        intent: { overlayIntentKind: 'action-triggered', actionId: current < target ? 'replay-next' : 'replay-prev' },
    }) ?? state;
    if (!next.pendingReplayMove) {
        if (next.traceSelection.replayPositionIndex === target) return readingState(next);
        if (next.traceSelection.replayPositionIndex !== state.traceSelection.replayPositionIndex) {
            return advanceTowards({ ...args, runtimeState: next }, base, target);
        }
        throw new Error('Episode navigation could not advance to its landmark.');
    }
    return { ...next, playbackAdvanceMode: 'automatic',
        isPlaying: next.pendingReplayMove.eventKind === 'primitive-operation' || state.episode?.isPlaying === true };
}

export function createEpisodeController(base: Controller): Controller {
    const reduce: NonNullable<Controller['reduceOverlayIntent']> = (args) => {
        const { intent, runtimeState } = args;
        if (intent.overlayIntentKind === 'select-changed' && intent.sectionId === 'episode-selector') {
            if (runtimeState.pendingReplayMove || runtimeState.isPlaying) return runtimeState;
            if (intent.value === '') return detachWorkflowEpisode(runtimeState);
            return enterScene(args, base, intent.value, 0);
        }
        const playback = runtimeState.episode;
        if (!playback) return base.reduceOverlayIntent?.(args) ?? runtimeState;
        if (intent.overlayIntentKind === 'select-changed' && intent.sectionId === 'episode-beat') {
            const { episode } = resolveWorkflowEpisode(playback.episodeHandle, runtimeState.sceneIndex ?? 0);
            const target = resolveEpisodeBeatSelection(episode.scenes, intent.value);
            const selected = target.sceneIndex === (runtimeState.sceneIndex ?? 0) ? runtimeState
                : enterScene(args, base, playback.episodeHandle, target.sceneIndex);
            return moveToBeat({ ...args, runtimeState: { ...selected, episode: { ...selected.episode!,
                playbackAdvanceMode: 'manual', narrationRequested: true, isPlaying: false } } }, base, target.beatIndex, true);
        }
        if (intent.overlayIntentKind === 'checkbox-changed' && intent.sectionId === 'episode-sound') {
            const next = { ...runtimeState, episode: { ...playback, soundEnabled: intent.checked } };
            // An error (such as a missing recording) restarts the beat once sound no longer requires the recording.
            return playback.error && !runtimeState.pendingReplayMove ? moveToBeat({ ...args, runtimeState: { ...next,
                episode: { ...next.episode, narrationRequested: true, isPlaying: false } } }, base, playback.beatIndex) : next;
        }
        const subtitleState = reduceEpisodeSubtitleIntent(playback, intent);
        if (subtitleState) return { ...runtimeState, episode: subtitleState };
        if (intent.overlayIntentKind !== 'action-triggered') return runtimeState;
        if (intent.actionId === 'episode-play-pause') {
            // Completed concept demonstrations need a fresh timeline when played again.
            const restartCycle = playback.narrationCompleted
                && (runtimeState.conceptArrangement?.content === 'tool-types'
                    || runtimeState.conceptArrangement?.content === 'sum-resources'
                    || runtimeState.conceptArrangement?.content === 'tool-invocation');
            if ((playback.error || restartCycle) && !playback.isPlaying && !runtimeState.pendingReplayMove) {
                return moveToBeat({ ...args, runtimeState: { ...runtimeState, episode: { ...playback,
                    playbackAdvanceMode: 'automatic', isPlaying: true } } }, base, playback.beatIndex);
            }
            return { ...runtimeState, episode: { ...playback, playbackAdvanceMode: 'automatic',
                visualRequested: true, isPlaying: !playback.isPlaying } };
        }
        if (intent.actionId === 'episode-reset') {
            return enterScene(args, base, playback.episodeHandle, 0);
        }
        if (intent.actionId === 'episode-cue-continue') {
            if (runtimeState.pendingReplayMove) return runtimeState;
            const { scene } = resolveWorkflowEpisode(playback.episodeHandle, runtimeState.sceneIndex ?? 0);
            const beat = scene.beats[playback.beatIndex];
            const cueIndex = playback.cueIndex ?? 0;
            if (!beat?.cues?.[cueIndex] || (playback.narrationCueReached ?? -1) < cueIndex
                || playback.status !== 'reading') return runtimeState;
            const episode = { ...playback, cueIndex: cueIndex + 1 };
            if (scene.sceneKind === 'concept') {
                return { ...runtimeState, episode,
                    conceptArrangement: conceptPresentationAt(scene.beats[playback.beatIndex]!, episode).arrangement };
            }
            const state = { ...runtimeState, episode: { ...episode, status: 'moving' as const } };
            return advanceTowards({ ...args, runtimeState: state }, base, traceTarget(args.cosmosData, state));
        }
        if (intent.actionId === 'episode-next' || intent.actionId === 'episode-prev' || intent.actionId === 'episode-continue') {
            if (intent.actionId === 'episode-continue' && runtimeState.pendingReplayMove) return runtimeState;
            return moveToBeat({ ...args, runtimeState: { ...runtimeState, episode: {
                ...playback,
                playbackAdvanceMode: intent.actionId === 'episode-continue' ? playback.playbackAdvanceMode : 'manual',
                narrationRequested: intent.actionId !== 'episode-continue',
                isPlaying: intent.actionId === 'episode-continue' && playback.isPlaying,
            } } }, base, playback.beatIndex + (intent.actionId === 'episode-prev' ? -1 : 1),
            intent.actionId !== 'episode-continue');
        }
        return runtimeState;
    };
    return {
        ...base,
        reducePlaybackSuspension: (args) => {
            const next = base.reducePlaybackSuspension?.(args) ?? args.runtimeState;
            return next.episode ? { ...next, episode: { ...next.episode,
                isPlaying: !args.suspended && args.resumeState.episode?.isPlaying === true,
            } } : next;
        },
        reduceOverlayIntent: reduce,
        buildOverlayModel: (args) => {
            const model = args.runtimeState.conceptArrangement
                ? { overlayId: 'workflow-runner', title: 'WorkflowSpace', sections: [] }
                : base.buildOverlayModel?.({ ...args, config: withWorkflowSceneConfig(args.config, args.runtimeState) });
            if (!model) return model;
            const episodes = getAvailableWorkflowEpisodes(args.cosmosData);
            const playback = args.runtimeState.episode;
            const busy = args.runtimeState.pendingReplayMove !== null;
            const resolved = playback ? resolveWorkflowEpisode(playback.episodeHandle, args.runtimeState.sceneIndex ?? 0) : undefined;
            const episode = resolved?.episode;
            const scene = resolved?.scene;
            const beat = scene?.beats[playback?.beatIndex ?? 0];
            const canNext = Boolean(playback && episode && scene && (
                playback.beatIndex < scene.beats.length - 1
                || (args.runtimeState.sceneIndex ?? 0) < episode.scenes.length - 1));
            return { ...model, title: episode?.title ?? model.title,
                ...(playback && episode && scene && beat ? { timeline: {
                    label: 'Episode timeline', positionSectionId: 'episode-beat', controlsSectionId: 'episode-playback',
                } } : {}),
                sections: [
                { sectionKind: 'select', sectionId: 'episode-selector', label: 'Episode',
                    selectedValue: playback?.episodeHandle ?? '', enabled: !busy && !playback?.isPlaying,
                    options: [{ value: '', label: 'Normal trace playback' }, ...episodes.map((episode) => ({
                        value: episode.handle, label: episode.title,
                    }))] },
                ...(playback && episode && scene && beat ? [
                    episodeBeatSection(episode.scenes, args.runtimeState.sceneIndex ?? 0, playback.beatIndex),
                    ...episodeSubtitleSections(playback), {
                    sectionKind: 'actions' as const, sectionId: 'episode-playback',
                    label: `${scene.title} (${(args.runtimeState.sceneIndex ?? 0) + 1}/${episode.scenes.length}; ${playback.beatIndex + 1}/${scene.beats.length})`,
                    description: playback.status === 'moving' ? 'Moving' : 'Ready',
                    actions: [
                        { actionId: 'episode-play-pause', label: playback.isPlaying ? 'Pause' : 'Play',
                            playbackRole: 'toggle' as const,
                            isActive: playback.isPlaying,
                            enabled: playback.isPlaying || canNext || (scene.sceneKind === 'trace' && scene.excerpt !== undefined)
                                || (scene.sceneKind === 'concept'
                                    && (args.runtimeState.conceptArrangement?.content === 'tool-types'
                                        || args.runtimeState.conceptArrangement?.content === 'sum-resources'
                                        || args.runtimeState.conceptArrangement?.content === 'tool-invocation')) },
                        { actionId: 'episode-reset', playbackRole: 'reset' as const, label: 'Reset', enabled: true },
                        { actionId: 'episode-prev', playbackRole: 'step' as const, label: 'Prev', allowDuringPlayback: true,
                            enabled: playback.beatIndex > 0 || (args.runtimeState.sceneIndex ?? 0) > 0 },
                        { actionId: 'episode-next', playbackRole: 'step' as const, label: 'Next', allowDuringPlayback: true,
                            enabled: canNext || workflowEpisodes.indexOf(episode) < workflowEpisodes.length - 1 },
                    ],
                }, ...(playback.error ? [{
                    sectionKind: 'actions' as const, sectionId: 'episode-error', label: 'Episode error',
                    description: playback.error, actions: [],
                }] : [])] : []),
                ...model.sections.filter((section) => !playback || section.sectionId === 'runner-status'),
            ], ...withSceneSubtitle(playback, beat) };
        },
        getRuntimeTickIntervalMs: (args) => args.runtimeState.episode ? undefined : base.getRuntimeTickIntervalMs?.(args),
        reduceRuntimeTick: (args) => args.runtimeState.episode ? args.runtimeState : base.reduceRuntimeTick?.(args) ?? args.runtimeState,
        reduceDerivationTransitionComplete: (args) => {
            let next = base.reduceDerivationTransitionComplete?.({ ...args,
                config: withWorkflowSceneConfig(args.config, args.runtimeState) }) ?? args.runtimeState;
            const playback = next.episode;
            if (!playback || playback.status !== 'moving') return next;
            if (next.pendingReplayMove?.eventKind === 'primitive-operation'
                && next.pendingReplayMove.arithmeticProgress.pendingStepIndex === null) {
                next = base.reduceOverlayIntent?.({ ...args, config: withWorkflowSceneConfig(args.config, next),
                    runtimeState: { ...next, isPlaying: false },
                    intent: { overlayIntentKind: 'action-triggered', actionId: 'replay-next' } }) ?? next;
                return { ...next, isPlaying: true, playbackAdvanceMode: 'automatic' };
            }
            if (next.pendingReplayMove) return next;
            return advanceTowards({ ...args, runtimeState: next }, base, traceTarget(args.cosmosData, next));
        },
    };
}

export function createEpisodeEffectOwner(
    controller: Controller,
    narration = createBrowserEpisodeNarrationOwner(),
) {
    const cycle = createConceptCyclePlaybackOwner();
    const episode = createEpisodePlaybackEffectOwner({
        select: (args: Args & { playbackActive?: boolean; animationSuspended?: boolean }) => {
            const playback = args.runtimeState.episode!;
            const { scene } = resolveWorkflowEpisode(playback.episodeHandle, args.runtimeState.sceneIndex ?? 0);
            const beat = scene.beats[playback.beatIndex];
            const update = args.config.runtimeCallbacks?.onRuntimeStateChange;
            if (!beat || !update) throw new Error('Episode playback requires a beat and runtime update callback.');
            const matches = (state: object): state is RuntimeState => {
                if (!('adapterMode' in state) || state.adapterMode !== 'runner'
                    || !('episode' in state) || !state.episode || typeof state.episode !== 'object') return false;
                return 'episodeHandle' in state.episode && state.episode.episodeHandle === playback.episodeHandle
                    && 'beatIndex' in state.episode && state.episode.beatIndex === playback.beatIndex
                    && 'revision' in state.episode && state.episode.revision === playback.revision
                    && 'sceneIndex' in state && state.sceneIndex === args.runtimeState.sceneIndex;
            };
            return { sceneId: scene.id, beat, recording: { clips: narrationManifest.clips, catalog: 'workflow' },
                update: (reduce: (state: RuntimeState) => RuntimeState) => update(state => matches(state) ? reduce(state) : state) };
        },
        continue: (args, state, action) => controller.reduceOverlayIntent?.({ ...args, runtimeState: state,
            intent: { overlayIntentKind: 'action-triggered',
                actionId: action === 'cue' ? 'episode-cue-continue' : 'episode-continue' } }) ?? state,
    }, narration);
    return Object.assign((args: Parameters<typeof episode>[0]) => {
        episode(args);
        cycle(args);
    }, {
        dispose: () => { cycle.dispose(); episode.dispose(); },
        spokenProgress: episode.spokenProgress,
        secondsSinceCue: episode.secondsSinceCue,
    });
}
import narrationManifest from '@/_data/workflowNarrationManifest';
